<template>
  <Popup
    ref="popup"
    :instance-id="instanceId"
    module-name="OptinPopup"
    class="optin-popup"
    data-module="optin-popup"
    :trigger-type="triggerType"
    :delay-to-show="delayToShow"
    :show-on-desk="showOnDesk"
    :show-on-mobile="showOnMobile"
    :style="{
      zIndex,
      '--cta-color': ctaColor,
      '--background-color': backgroundColor,
      '--text-color': textColor,
      '--title-weight': titleWeight,
    }"
  >
    <div class="optin-popup__container">
      <div class="optin-popup__content">
        <div class="optin-popup__image">
          <LImage
            :src="image"
            :alt="imageAlt"
          />
        </div>
        <div class="optin-popup__form-content">
          <h2 v-if="title">
            {{ title }}
          </h2>
          <p v-if="subtitle">
            {{ subtitle }}
          </p>
          <form
            class="form"
            novalidate
            @submit.prevent="send"
          >
            <div
              class="form__field"
              :class="{ error: !isValidContactName }"
            >
              <FloatingLabel :for-attr="`name-${moduleId}`">
                {{ $t('placeHolderName') }}
              </FloatingLabel>
              <input
                :id="`name-${moduleId}`"
                v-model.trim="contactName"
                name="name"
                type="text"
                @blur="startValidate('name')"
              />
            </div>
            <div
              class="form__field"
              :class="{ error: !isValidContactSurname }"
            >
              <FloatingLabel :for-attr="`surname-${moduleId}`">
                {{ $t('placeHolderLastName') }}
              </FloatingLabel>
              <input
                :id="`surname-${moduleId}`"
                v-model.trim="contactSurname"
                name="surname"
                type="text"
                @blur="startValidate('surname')"
              />
            </div>
            <div
              class="form__field"
              :class="{ error: !isValidContactEmail }"
            >
              <FloatingLabel :for-attr="`email-${moduleId}`">
                E-mail*
              </FloatingLabel>
              <input
                :id="`email-${moduleId}`"
                v-model.trim="contactEmail"
                name="email"
                type="email"
                @blur="startValidate('email')"
              />
            </div>
            <div
              v-for="(campoCoringa, index) in camposCoringa"
              :key="index"
              class="form__field"
              :class="{
                error: !isValidContactCustomField(index),
              }"
            >
              <!-- TODO dynamic inputs for custom-fields -->
              <FloatingLabel
                v-if="campoCoringa.fieldType !== 'BIT'"
                :for-attr="`custom-field-${index}-${moduleId}`"
              >
                {{ campoCoringa.campoCoringaLabel
                }}{{ getRequiredSuffix(campoCoringa) }}
              </FloatingLabel>
              <select
                v-if="campoCoringa.crmTargetField === 'Estado'"
                :id="`custom-field-${index}-${moduleId}`"
                v-model="contactCustomFields[index]"
                :name="`custom-field-${index}`"
                class="custom-field-select"
                @blur="startValidate(`custom-field-${index}`)"
              >
                <option
                  value=""
                  hidden
                ></option>
                <option
                  v-for="state in states"
                  :key="state.code"
                  :value="state.code"
                >
                  {{ state.name }}
                </option>
              </select>
              <select
                v-else-if="campoCoringa.fieldType === 'COMBO'"
                :id="`custom-field-${index}-${moduleId}`"
                v-model="contactCustomFields[index]"
                :name="`custom-field-${index}`"
                class="custom-field-select"
                @blur="startValidate(`custom-field-${index}`)"
              >
                <option
                  value=""
                  hidden
                ></option>
                <option
                  v-for="(campo, idx) in campoCoringa.options"
                  :key="idx"
                >
                  {{ campo }}
                </option>
              </select>
              <div v-else-if="campoCoringa.fieldType === 'BIT'">
                <label
                  :for="`custom-field-${index}-${instanceId}`"
                  class="form__optin"
                >
                  <Checkbox
                    :id="`custom-field-${index}-${instanceId}`"
                    v-model="contactCustomFields[index]"
                    :color="ctaColor"
                  />
                  <div class="optin-text">
                    {{ campoCoringa.campoCoringaLabel }}
                  </div>
                </label>
              </div>
              <ImaskInput
                v-else
                :id="`custom-field-${index}-${moduleId}`"
                v-model="contactCustomFields[index]"
                :mask="getMask(campoCoringa)"
                :name="`custom-field-${index}`"
                type="text"
                @blur="startValidate(`custom-field-${index}`)"
              />
            </div>
            <div :class="{ error: !isValidContactOptin }">
              <label
                ref="consentText"
                :for="optinId"
                class="form__optin"
              >
                <Checkbox
                  :id="optinId"
                  v-model="contactOptin"
                  :color="ctaColor"
                  @change="startValidate('optin')"
                />
                <div
                  class="optin-text"
                  v-html="consentText"
                ></div>
              </label>
            </div>
            <div class="form__footer">
              <div
                class="form__message"
                :class="{ 'form__message--error': !wasSentWithSuccess }"
              >
                <span v-show="!wasSentWithSuccess">
                  {{ $t('errorMessage') }}
                </span>
              </div>
              <button
                type="submit"
                class="cta"
                :class="{ cta__sent: wasSent }"
                :disabled="isSending"
              >
                {{ submitButtonLabel }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </Popup>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { IMaskComponent } from 'vue-imask';

import { pushDataLayer } from '@/helpers/gtm';
import uniqueId from '@/helpers/uniqueId';
import weightOptions from '@/helpers/weightOptions.json';
import states from '@/helpers/brazilianStates.json';
import isValidEmail from '@/validation/validators/email';
import isValidCpf from '@/validation/validators/cpf';
import isValidDate from '@/validation/validators/date';
import FloatingLabel from '@/components/site/FloatingLabel.vue';
import Checkbox from '@/components/site/Checkbox.vue';
import Popup from '../generic/modals/Popup.vue';

export default {
  name: 'OptinPopup',
  components: {
    FloatingLabel,
    Checkbox,
    Popup,
    ImaskInput: IMaskComponent,
  },
  inheritAttrs: false,
  props: {
    ...Popup.props,
    instanceId: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    titleWeight: {
      type: String,
      default: '600',
    },
    subtitle: {
      type: String,
      default: '',
    },
    backgroundColor: {
      type: String,
      default: '',
    },
    textColor: {
      type: String,
      default: '',
    },
    formTitle: {
      type: String,
      default: '',
    },
    successMessage: {
      type: String,
      default: '',
    },
    consentText: {
      type: String,
      default: '',
    },
    ctaLabel: {
      type: String,
      default: '',
    },
    ctaColor: {
      type: String,
      default: '',
    },
    imageDesk: {
      type: String,
      default: '',
    },
    imageMobile: {
      type: String,
      default: '',
    },
    imageDeskAlt: {
      type: String,
      default: '',
    },
    imageMobileAlt: {
      type: String,
      default: '',
    },
    useDifferentMobileBgImage: {
      type: Boolean,
      default: false,
    },
    idsCRMNovas: {
      type: Array,
      default() {
        return [];
      },
    },
    idsCRMExistentes: {
      type: Array,
      default() {
        return [];
      },
    },
    camposCoringa: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      contactName: '',
      contactSurname: '',
      contactEmail: '',
      contactOptin: false,
      optinId: null,
      contactCustomFields: [],
      isSending: false,
      validatingFields: [],
      wasSent: false,
      wasSentWithSuccess: true,
      isDesktop: true,
      states,
      camposCoringaApi: null,
    };
  },
  computed: {
    submitButtonLabel() {
      if (this.isSending) {
        return this.$t('sendingText');
      }

      if (this.wasSent && this.wasSentWithSuccess) {
        return this.successMessage;
      }

      return this.ctaLabel;
    },
    idsCRMNovasValid() {
      return this.idsCRMNovas.filter(this.isValidCrmGroupId);
    },
    idsCRMExistentesValid() {
      return this.idsCRMExistentes.filter(this.isValidCrmGroupId);
    },
    isValidContactName() {
      return !this.validatingFields.includes('name') || this.contactName !== '';
    },
    isValidContactSurname() {
      return (
        !this.validatingFields.includes('surname') || this.contactSurname !== ''
      );
    },
    isValidContactEmail() {
      return (
        !this.validatingFields.includes('email') ||
        isValidEmail(this.contactEmail)
      );
    },
    isValidContactOptin() {
      return !this.validatingFields.includes('optin') || this.contactOptin;
    },
    image() {
      if (this.isDesktop || !this.useDifferentMobileBgImage) {
        return this.imageDesk;
      }
      return this.imageMobile;
    },
    imageAlt() {
      if (
        this.isDesktop ||
        !this.useDifferentMobileBgImage ||
        !this.imageMobileAlt
      ) {
        return this.imageDeskAlt;
      }
      return this.imageMobileAlt;
    },
    moduleId() {
      return this.instanceId.replace(/\D/g, '');
    },
    ...mapGetters(['settings']),
    ...mapState(['userEmail', 'customer', 'apiBaseUrl', 'activePageId']),
  },
  watch: {
    camposCoringa: {
      // exclue o parâmetro fieldType do campo se ele não é Coringa, e define 'string' como padrão se for
      handler(newValue) {
        newValue.forEach((campo) => {
          if (!campo.crmTargetField.includes('Coringa')) {
            // eslint-disable-next-line no-param-reassign
            campo.fieldType = null;
          } else {
            const fieldConfig = this.camposCoringaApi.find(
              (each) => campo.crmTargetField === each.Campo,
            );

            if (fieldConfig) {
              // eslint-disable-next-line no-param-reassign
              campo.fieldType = fieldConfig.Tipo;

              // eslint-disable-next-line no-param-reassign
              campo.fieldDesc = fieldConfig.Descricao_Coringa;
              // eslint-disable-next-line no-param-reassign
              campo.options =
                campo.fieldType === 'COMBO' ? fieldConfig.CamposNaCombo : null;
              // eslint-disable-next-line no-param-reassign
              campo.isOptional = campo.fieldType === 'BIT';
            }
          }
        });
      },
      deep: true,
    },
  },
  created() {
    this.requestCampoCoringa();
    this.contactCustomFields = this.camposCoringa.map((campo) => {
      if (campo.fieldType === 'BIT') {
        return false;
      }
      return '';
    });
  },
  mounted() {
    this.optinId = uniqueId();

    this.isDesktop = window.matchMedia('(min-width: 768px)').matches;
    window.addEventListener('resize', () => {
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;

      // If "device" changed...
      if (isDesktop !== this.isDesktop) {
        this.isDesktop = isDesktop;
      }
    });
  },
  methods: {
    isValid() {
      this.startValidate('name');
      this.startValidate('surname');
      this.startValidate('email');
      this.startValidate('optin');

      let contactCustomFieldsAreValid = true;

      this.camposCoringa.forEach((campo, index) => {
        this.startValidate(`custom-field-${index}`);

        contactCustomFieldsAreValid =
          contactCustomFieldsAreValid && this.isValidContactCustomField(index);
      });

      return (
        this.isValidContactName &&
        this.isValidContactSurname &&
        this.isValidContactEmail &&
        this.isValidContactOptin &&
        contactCustomFieldsAreValid
      );
    },
    isValidState(state) {
      return /^[A-Z]{2}$/.test(state);
    },
    isValidPhone(phone) {
      // Phones must have at least 10 numbers
      return phone.replace(/\D/g, '').length >= 10;
    },
    isValidCrmGroupId(id) {
      return typeof id === 'string' && /^\d+$/.test(id.trim());
    },
    requestCampoCoringa() {
      fetch('https://api-crm.pmweb.com.br/v2/api/Coringa/', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          TokenPmweb: this.settings.crmToken,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.Status.Code === 401) {
            this.camposCoringaApi = [];
            console.warn('Token de CRM inválido:', data.Warning.Message);
            return;
          }

          this.camposCoringaApi = data.Coringa.map((field) => {
            if (field.Tipo === 'COMBO') {
              // O array de opções vem como string e com aspas simples
              // Ex.: ['amarelo','azul']
              const parsedArray = JSON.parse(
                field.CamposNaCombo.replace(/'/g, '"'),
              );
              // eslint-disable-next-line no-param-reassign
              field.CamposNaCombo = parsedArray;
            }
            return field;
          });
        })
        .catch((error) => {
          console.log('ocorreu um erro', error);
        });
    },
    send() {
      this.wasSent = false;

      if (this.isValid()) {
        this.isSending = true;

        const body = {
          sendEmail: false,
          integrated: true,
          id: this.activePageId,
          url: window.location.href,
          data: {
            name: {
              value: this.contactName,
              label: 'Nome',
            },
            surname: {
              value: this.contactSurname,
              label: 'Sobrenome',
            },
            email: {
              value: this.contactEmail,
              label: 'E-mail',
            },
            consentText: {
              value: this.$refs.consentText.textContent,
              label: 'Texto de consentimento',
            },
            acceptedConsent: {
              value: this.contactOptin,
              label: 'Aceitou o consentimento',
            },
          },
          crmData: {
            groups: this.idsCRMNovasValid.map((id) => ({
              Numgrupo: Number(id),
            })),
            alternativeGroups: this.idsCRMExistentesValid.map((id) => ({
              Numgrupo: Number(id),
            })),
          },
        };

        this.contactCustomFields.forEach((value, idx) => {
          const config = this.camposCoringa[idx];
          const key = config.crmTargetField || `Coringa${idx + 1}`;
          const label = config.campoCoringaLabel;
          const fieldObject = {
            value,
            label,
          };

          if (key === '__birthdate__') {
            if (isValidDate(value)) {
              // altera key para "birthdate"
              body.data.birthdate = fieldObject;
            }
          } else {
            if (config.fieldType === 'DATETIME') {
              const [day, month, year] = value.split('/');
              const isoDate = new Date(year, month - 1, day).toISOString();

              fieldObject.value = isoDate;
            } else if (config.fieldType === 'FLOAT') {
              fieldObject.value = value.replace(',', '.');
            }

            body.data[key] = fieldObject;
          }
        });

        fetch(`${this.apiBaseUrl}contact/${this.customer}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
          },
          body: JSON.stringify(body),
        })
          .then((res) => {
            this.wasSentWithSuccess = res.status === 200;

            if (this.wasSentWithSuccess) {
              this.handleSuccessSubmit();
            }
          })
          .catch(() => {
            this.wasSentWithSuccess = false;
          })
          .finally(() => {
            this.isSending = false;
            this.wasSent = true;
          });
      }
    },
    resetForm() {
      this.contactName = '';
      this.contactSurname = '';
      this.contactEmail = '';
      this.contactOptin = false;
      this.contactCustomFields = this.contactCustomFields.map(() => '');
      this.validatingFields = [];
    },
    startValidate(field) {
      if (!this.validatingFields.includes(field)) {
        this.validatingFields.push(field);
      }
    },
    isValidContactCustomField(index) {
      const { crmTargetField, fieldType } = this.camposCoringa[index];
      const value = this.contactCustomFields[index];

      if (fieldType === 'BIT') {
        return true;
      }
      // If field is being validated...
      if (this.validatingFields.includes(`custom-field-${index}`)) {
        if (
          value === '' &&
          this.isOptionalCustomField(this.camposCoringa[index])
        ) {
          return true;
        }

        if (crmTargetField === 'CPF') {
          return isValidCpf(value);
        }

        if (crmTargetField === '__birthdate__' || fieldType === 'DATETIME') {
          return isValidDate(value);
        }

        if (crmTargetField === 'Estado') {
          return this.isValidState(value);
        }

        if (crmTargetField === 'Telefone') {
          return this.isValidPhone(value);
        }

        return typeof value === 'string' && value !== '';
      }

      return true;
    },
    isOptionalCustomField(customField) {
      return customField.isOptional === true;
    },
    getRequiredSuffix(customField) {
      return this.isOptionalCustomField(customField) ? '' : '*';
    },
    getMask({ fieldType, crmTargetField }) {
      if (crmTargetField === 'CPF') {
        return '000.000.000-00';
      }
      if (crmTargetField === '__birthdate__') {
        return '00/00/0000';
      }

      switch (fieldType) {
        case 'date':
        case 'DATETIME':
          return '00/00/0000';
        case 'INT':
          return /^\d+$/;
        case 'FLOAT':
          return /^\d+(,\d{0,2})?$/;
        default:
          return /^.+$/;
      }
    },
    handleSuccessSubmit() {
      pushDataLayer({ event: 'contact-sent' });
      this.resetForm();
      this.wasSent = true;
      localStorage.setItem(this.$refs.popup.submittedStorageName, 1);

      setTimeout(() => {
        // this.$refs.popup.closeModal(); // não existe no editor
        this.wasSent = false; // go back to original cta label

        // Solução temporária para que o Widget carregue o email para o motor
        this.$router
          .replace({ query: { email: this.userEmail } })
          .catch(() => {})
          .finally(() => window.location.reload());
      }, 5000);
    },
  },
  metadata: {
    extends: Popup.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            label: 'Texto',
            position: 0,
            fields: [
              {
                name: 'title',
                type: 'ShortText',
                label: 'Título',
              },
              {
                name: 'titleWeight',
                type: 'VueSelect',
                label: 'Peso do título',
                default: '300',
                options: weightOptions,
                isEnabled({ title }) {
                  return title;
                },
              },
              {
                name: 'subtitle',
                type: 'ShortText',
                label: 'Subtítulo',
              },
            ],
          },
          {
            name: 'form',
            label: 'Formulário',
            position: 1,
            fields: [
              {
                name: 'successMessage',
                type: 'ShortText',
                label: 'Mensagem de confirmação',
              },
              {
                name: 'consentText',
                type: 'Html',
                maxlength: 200,
                label: 'Texto de consentimento',
              },
              {
                name: 'ctaLabel',
                type: 'ShortText',
                label: 'Texto do botão',
              },
            ],
          },
          {
            name: 'crmGroups',
            label: 'Grupos do CRM',
            fields: [
              {
                name: 'idsCRMNovas',
                type: 'Tags',
                label: 'IDs - Pessoas novas',
                helpText:
                  'Grupos de leads que se cadastraram pela primeira vez',
                pattern: /^\d+$/,
                validation: {
                  presence: {
                    allowEmpty: false,
                    message:
                      'Adicione, no mínimo, um grupo de CRM para pessoas novas',
                  },
                },
              },
              {
                name: 'idsCRMExistentes',
                type: 'Tags',
                label: 'IDs - Pessoas existentes',
                helpText: 'Grupos de leads que se cadastraram novamente',
                pattern: /^\d+$/,
                validation: {
                  presence: {
                    allowEmpty: false,
                    message:
                      'Adicione, no mínimo, um grupo de CRM para pessoas existentes',
                  },
                },
              },
            ],
          },
          {
            name: 'extraFields',
            label: 'Campos extras',
            fields: [
              {
                name: 'camposCoringa',
                type: 'List',
                maxItems: 4,
                itemLabel({ campoCoringaLabel }) {
                  return campoCoringaLabel || 'Campo extra {index}';
                },
                addItemLabel: 'Adicionar campo extra',
                fields: [
                  {
                    helpText() {
                      const hashMapFieldType = {
                        VARCHAR: 'Texto livre',
                        FLOAT: 'Número com vírgula',
                        INT: 'Número inteiro',
                        COMBO: 'Lista de seleção',
                        DATETIME: 'Data',
                        BIT: 'Checkbox',
                      };
                      if (this.crmTargetField.includes('Coringa')) {
                        return `<strong>Descrição:</strong> ${
                          this.fieldDesc || ''
                        } <br> <strong>Tipo:</strong> ${
                          hashMapFieldType[this.fieldType] || ''
                        } <br> ${
                          this.options !== null
                            ? `<strong> Opções:</strong> ${this.options.join(
                                ', ',
                              )}`
                            : ''
                        }`;
                      }
                      return '';
                    },
                    name: 'crmTargetField',
                    type: 'VueSelect',
                    label: 'Campo correspondente no CRM',
                    default: '',
                    options() {
                      const coringasToOptions = (cs) =>
                        cs.map((cd) => ({
                          label: cd.Nome_Coringa || cd.Campo,
                          value: cd.Campo,
                        }));
                      const commonFields = [
                        {
                          label: 'CPF',
                          value: 'CPF',
                        },
                        {
                          label: 'Cidade',
                          value: 'Cidade',
                        },
                        {
                          label: 'Estado',
                          value: 'Estado',
                        },
                        {
                          label: 'Data de nascimento',
                          value: '__birthdate__', // Head up! This is a special case
                        },
                      ];

                      // Are custom fields already loaded?
                      if (this.camposCoringaApi) {
                        return [
                          ...coringasToOptions(this.camposCoringaApi),
                          ...commonFields,
                        ];
                      }
                      // THIS IS PURE MAGIC
                      return new Promise((resolve) => {
                        const unwatch = this.$watch(
                          'camposCoringaApi',
                          (camposCoringaApi) => {
                            resolve([
                              ...coringasToOptions(camposCoringaApi),
                              ...commonFields,
                            ]);
                            unwatch();
                          },
                        );
                      });
                    },
                    validation: {
                      presence: {
                        allowEmpty: false,
                        message: 'Adicione, o campo correspondente no CRM',
                      },
                    },
                  },
                  {
                    name: 'campoCoringaLabel',
                    type: 'ShortText',
                    label: 'Nome do campo',
                    default: '',
                  },

                  {
                    name: 'isOptional',
                    type: 'Checkbox',
                    label: 'Preenchimento opcional',
                    default: false,
                    isEnabled() {
                      return this.fieldType !== 'BIT';
                    },
                  },
                ],
              },
            ],
          },
          {
            name: 'image',
            label: 'Imagem',
            after: 'trigger',
            fields: [
              {
                name: 'imageDesk',
                type: 'ImageFile',
                label: 'Imagem',
              },
              {
                name: 'imageDeskAlt',
                type: 'ShortText',
                label: 'Texto alternativo para a imagem',
              },
              {
                name: 'useDifferentMobileBgImage',
                type: 'Checkbox',
                label: 'Usar uma imagem diferente para mobile',
              },
              {
                name: 'imageMobile',
                type: 'ImageFile',
                label: 'Imagem (mobile)',
                isEnabled({ useDifferentMobileBgImage }) {
                  return useDifferentMobileBgImage;
                },
              },
              {
                name: 'imageMobileAlt',
                type: 'ShortText',
                label: 'Texto alternativo para a imagem mobile',
                isEnabled({ useDifferentMobileBgImage }) {
                  return useDifferentMobileBgImage;
                },
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        label: 'Aparência',
        groups: [
          {
            name: 'colors',
            label: 'Cores',
            fields: [
              {
                name: 'backgroundColor',
                type: 'ColorPicker',
                label: 'Cor de fundo',
              },
              {
                name: 'textColor',
                type: 'ColorPicker',
                label: 'Cor do texto',
              },
              {
                name: 'ctaColor',
                type: 'ColorPicker',
                label: 'Cor do botão',
              },
            ],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        placeHolderName: 'Nome*',
        placeHolderLastName: 'Sobrenome*',
        errorMessage: 'Ops! Algo errado aconteceu ao enviar seu contato.',
        sendingText: 'Enviando...',
      },
      en: {
        placeHolderName: 'Name*',
        placeHolderLastName: 'Last name*',
        errorMessage:
          'Oops! Something wrong happened when sending your contact.',
        sendingText: 'Sending...',
      },
      es: {
        placeHolderName: 'Nombre*',
        placeHolderLastName: 'Apellido*',
        errorMessage: '¡UPS! Ocurrió algo incorrecto al enviar su contacto.',
        sendingText: 'Enviando...',
      },
    },
  },
};
</script>

<style scoped lang="scss">
.optin-popup {
  padding: 30px 40px;

  &__container {
    background-color: var(--background-color);
    height: 100%;
    overflow: auto; // if the user screen's height is too small, allow to scroll

    @include media-breakpoint-up(md) {
      &::-webkit-scrollbar {
        width: 10px;
      }
      &::-webkit-scrollbar-track {
        border-radius: 10px;
      }
      &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.4);
        border-radius: 10px;
      }
      &::-webkit-scrollbar-thumb:hover {
        background: #1d1d1d;
      }
    }
  }

  &__content {
    @include media-breakpoint-up(md) {
      display: flex;
    }
  }

  &__image {
    flex-shrink: 0;

    .img {
      display: block;
    }

    @include media-breakpoint-up(xs) {
      height: 200px;
    }

    @include media-breakpoint-up(md) {
      height: auto;
      width: 300px;
    }
  }

  &__form-content {
    color: var(--text-color);
    padding: 24px 32px;

    @include media-breakpoint-up(md) {
      padding: 60px 60px 50px 54px;
    }

    > h2 {
      color: var(--text-color);
      margin-bottom: 12px;
      font-size: 20px;
      font-weight: var(--title-weight, 600);
      line-height: 28px;

      @include media-breakpoint-up(md) {
        font-size: 24px;
      }
    }

    > p {
      font-size: $size-small;
      line-height: 18px;
      opacity: 0.8;
    }

    .form {
      width: 100%;

      &__field {
        position: relative;
        width: 100%;
        margin-top: 16px;

        input,
        select {
          border: 1px solid $lb-medium-gray;
          background-color: $lb-white;
          border-radius: 4px;
          width: 100%;
          font-style: normal;
          font-size: $size-normal;
          line-height: 24px;
          outline: none;
          padding: 18px 14px 4px;

          &.custom-field-select {
            min-height: 48px;
          }

          &:focus {
            border-color: $lb-black;
          }
        }

        label {
          font-size: $size-normal;
        }
      }

      .error {
        label {
          color: $lb-red;
          font-weight: $lb-semibold;
        }

        input,
        select {
          border-color: $lb-red !important;
          color: $lb-red;
          font-weight: $lb-semibold;
        }
      }

      &__optin {
        display: flex;
        align-items: center;
        margin-top: 24px;
        text-align: left;
        line-height: 16px;
        cursor: pointer;

        .checkbox {
          flex-shrink: 0;
          margin-right: 12px;
        }

        .optin-text {
          opacity: 0.8;
          font-size: $size-small;
        }
      }

      .checkbox {
        input {
          width: auto;
        }
      }

      &__footer {
        margin-top: 24px;

        .cta {
          width: 100%;
          border-radius: 3px;
          background-color: var(--cta-color);
          color: $lb-white;
          font-size: $size-normal;
          text-transform: none;

          &:focus {
            outline: 1px solid $lb-black;
          }

          &__sent {
            transition: background 1s;
            background-color: #15a057 !important;

            &:hover::after {
              transform: scaleX(0); // remove hover animation
            }
          }
        }
      }

      &__message {
        &--success {
          color: green;
        }

        &--error {
          color: crimson;
        }
      }
    }
  }
}
</style>
